import {
  CONSUMPTION_TAX_INCLUDED_COUNTRIES,
  GST_INCLUDED_COUNTRIES,
  NO_TAX_COUNTRIES,
  TAX_EXCLUDED_COUNTRIES,
  VAT_INCLUDED_COUNTRIES,
} from './countries';

type TaxMessageKey =
  | 'consumptionIncluded'
  | 'vatIncluded'
  | 'gstIncluded'
  | 'salesTaxIncluded'
  | 'salesTaxExcluded';

type TaxLineKey = 'vat' | 'gst' | 'consumption' | 'salesTax';

/**
 * Determine which short tax badge message to display next to a price.
 *
 * Priority order matches production requirements:
 * 1. Consumption tax (JP)
 * 2. VAT (EU, UK, CH, etc.)
 * 3. GST (AU)
 * 4. Sales tax included (US/CA after tax calculated)
 * 5. Sales tax excluded (US/CA default)
 * 6. null (no-tax countries)
 */
export function getTaxMessage(countryCode: string, taxAmount?: number): TaxMessageKey | null {
  const code = countryCode.toUpperCase();

  if (includes(CONSUMPTION_TAX_INCLUDED_COUNTRIES, code)) return 'consumptionIncluded';
  if (includes(VAT_INCLUDED_COUNTRIES, code)) return 'vatIncluded';
  if (includes(GST_INCLUDED_COUNTRIES, code)) return 'gstIncluded';
  if (taxAmount && taxAmount > 0) return 'salesTaxIncluded';
  if (includes(TAX_EXCLUDED_COUNTRIES, code)) return 'salesTaxExcluded';
  return null;
}

/**
 * Determine the tax message for the order total footer.
 *
 * Differs from `getTaxMessage` in that it uses the net/gross comparison
 * to detect whether tax has been calculated (relevant for US/CA checkout).
 */
export function getTotalTaxMessage(
  netAmount: number,
  grossAmount: number,
  countryCode: string,
  hasShippingAddress?: boolean
): TaxMessageKey {
  const code = countryCode.toUpperCase();

  if (includes(CONSUMPTION_TAX_INCLUDED_COUNTRIES, code)) return 'consumptionIncluded';
  if (includes(VAT_INCLUDED_COUNTRIES, code)) return 'vatIncluded';

  if (netAmount < grossAmount) {
    return includes(GST_INCLUDED_COUNTRIES, code) ? 'gstIncluded' : 'salesTaxIncluded';
  }

  if (includes(TAX_EXCLUDED_COUNTRIES, code) && hasShippingAddress) {
    return 'salesTaxIncluded';
  }

  return 'salesTaxExcluded';
}

/**
 * Get the label key for the tax amount line in cart/checkout summaries.
 * Returns null for no-tax countries (line should be hidden).
 */
export function getTaxLineLabel(countryCode: string): TaxLineKey | null {
  const code = countryCode.toUpperCase();

  if (includes(CONSUMPTION_TAX_INCLUDED_COUNTRIES, code)) return 'consumption';
  if (includes(VAT_INCLUDED_COUNTRIES, code)) return 'vat';
  if (includes(GST_INCLUDED_COUNTRIES, code)) return 'gst';
  if (includes(TAX_EXCLUDED_COUNTRIES, code)) return 'salesTax';
  return null;
}

/**
 * Whether displayed prices should use the gross (tax-inclusive) amount.
 * Only TAX_EXCLUDED_COUNTRIES (US/CA) show net prices — all other countries
 * (including no-tax countries like HK/KW/QA) display gross.
 */
export function isTaxIncluded(countryCode: string): boolean {
  const code = countryCode.toUpperCase();
  return !includes(TAX_EXCLUDED_COUNTRIES, code);
}

/**
 * Whether the country is a no-tax jurisdiction where the tax line
 * and badge should be completely hidden.
 */
export function isNoTaxCountry(countryCode: string): boolean {
  return includes(NO_TAX_COUNTRIES, countryCode.toUpperCase());
}

/**
 * Whether the country is in a tax-excluded regime (US/CA) where
 * net prices are shown and tax is calculated at checkout.
 */
export function isTaxExcludedCountry(countryCode: string): boolean {
  return includes(TAX_EXCLUDED_COUNTRIES, countryCode.toUpperCase());
}

/**
 * Whether to show the "Sales tax calculated at checkout" note.
 * Only shown for US/CA in cart/drawer contexts before tax is computed.
 */
export function shouldShowTaxNote(
  countryCode: string,
  context: 'cart' | 'drawer' | 'checkout' | 'confirmation'
): boolean {
  if (context !== 'cart' && context !== 'drawer') return false;
  return isTaxExcludedCountry(countryCode);
}

/**
 * Determine the "tax info text" shown under the order total.
 * Returns the i18n message key to render, or null for no-tax countries.
 *
 * Behavior:
 * - VAT/GST/Consumption countries → corresponding "included" message (always)
 * - US/CA in cart → "salesTaxExcluded"
 * - US/CA at checkout with address or tax calculated → "salesTaxIncluded"
 * - No-tax countries → null (show nothing)
 */
export function getTotalInfoMessage(
  countryCode: string,
  options?: { taxCalculated?: boolean; hasShippingAddress?: boolean }
): TaxMessageKey | null {
  const code = countryCode.toUpperCase();

  if (includes(NO_TAX_COUNTRIES, code)) return null;
  if (includes(CONSUMPTION_TAX_INCLUDED_COUNTRIES, code)) return 'consumptionIncluded';
  if (includes(VAT_INCLUDED_COUNTRIES, code)) return 'vatIncluded';
  if (includes(GST_INCLUDED_COUNTRIES, code)) return 'gstIncluded';

  if (includes(TAX_EXCLUDED_COUNTRIES, code)) {
    if (options?.taxCalculated || options?.hasShippingAddress) {
      return 'salesTaxIncluded';
    }
    return 'salesTaxExcluded';
  }

  return null;
}

function includes(arr: readonly string[], value: string): boolean {
  return arr.includes(value);
}
